import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import errorIcon from "../../../../img/icons/error-icon.svg";
import localization from "../../../../data/config";
import {
  MAX_TYPE,
  MIN_TYPE,
  COMPARISONS,
  INVALID_RANGE_CHARACTERS,
  EMPTY_INPUT,
} from "../../../../constants/config/filters";
import {
  determineValue,
  checkRangeError,
  getInitialText,
} from "../../../../utilities/slider";
import {
  handleFocus,
  handleBlur as blurDragEnable,
} from "../../../../utilities/drag";
import "./style.css";
import { usePrevious } from "../../../../utilities/hooks";
import { parseMagnitude } from "../../../../utilities/magnitudes";
import {userEntryToNumber} from "../../../../utilities/quick-filters-validations";
import { expandScientific } from "../../../../utilities/numberUtils";
    /**
   * @name valueUnits
   * @function
   * @description Sets the fullform units
   */

      
   export const valueUnits = (value) => {
    if(value){
      const unit = value.toLowerCase();
  
      switch (unit) {
        case "v":
          return localization.v;
        case "a":
          return localization.a;
        case "sps":
          return localization.sps;
        case "dbfs":
          return localization.dbfs;
        case "db":
          return localization.db;
        case "lsbs":
          return localization.lsbs;
        case "w":
          return localization.w;
        case "mm":
          return localization.mm;
        case "v/rthz":
          return localization.vRthz;
        case "bits":
          return localization.bits;
        case "hz":
          return localization.hz;
        case "dbc":
          return localization.dbc;
        case "$ us":
          return localization.dollarUS;
        case "v p-p":
          return localization.vpp;
        case "mm²":
          return localization.mm2;
        case "v/us":
          return localization.vus;
        case "v/°c":
          return localization.vdeg;
        case "v-s":
          return localization.vs;
        case "ppm":
          return localization.ppm;
        case "ppm/degree c":
          return localization.degreeC;
        case "dbm":
          return localization.dbm;
        case "deg":
          return localization.deg;
        case "%":
          return localization.perc;
        default:
          return "";
      }
    }
  };

/**
 * @name Text
 * @function
 * @description Displays PST Range Boxes
 * @returns {JSX}
 * @param {*} props
 */
function Text(props) {
  const [max, setMax] = useState(() => {
    const initialMax = determineValue(props.magnitude, props.max);
    return initialMax === COMPARISONS.MAX ? props.max : initialMax;
  });

  const [min, setMin] = useState(() => {
    const initialMin = determineValue(props.magnitude, props.min); 
    return initialMin === COMPARISONS.MIN ? props.min : initialMin;
  });

  const [errors, setErrors] = useState([]),
    [trigger, setTrigger] = useState(false),
    prevMax = usePrevious(max),
    prevMin = usePrevious(min);

  const { change, qsfv, lastReset } = useSelector((state) => ({
    change: state.filters.lastChangeOrder,
    qsfv: state.qs.updated,
    lastReset: state.filters.lastReset,
  }));
  const prevChange = usePrevious(change),
    prevParam = usePrevious(props.parameter),
    prevQSFV = usePrevious(qsfv),
    prevLastReset = usePrevious(lastReset);
  let maxCompare = parseMagnitude(max);
  let minCompare = parseMagnitude(min);
  let rangeMax = parseMagnitude(props.rangeMax);
  let rangeMin = parseMagnitude(props.rangeMin);
  let minOutofRange =
    minCompare < rangeMin ||
    minCompare > rangeMax ||
    errorStateCheckForOnlyAlphabetsMin(minCompare) ||
    minCompare > maxCompare;
    
  let maxOutofRange =
    maxCompare > rangeMax ||
    maxCompare < rangeMin ||
    errorStateCheckForOnlyAlphabetsMax(maxCompare) ||
    maxCompare < minCompare;

  const isValueOutOfRange =
    minOutofRange ||
    maxOutofRange ||
    errorStateCheckForOnlyAlphabetsMin(minCompare) ||
    errorStateCheckForOnlyAlphabetsMax(maxCompare);

  function errorStateCheckForOnlyAlphabetsMin(min) {
    const regex = /^[a-zA-Z]*$|^[!@#$%^&*()_+]*$/;
    return regex.test(min);
  }
  function errorStateCheckForOnlyAlphabetsMax(max) {
    const regex = /^[a-zA-Z]*$|^[!@#$%^&*()_+]*$/;
    return regex.test(max);
  }

  let valueExceedingRange =
    minCompare > maxCompare
      ? `${minCompare} - ${maxCompare}`
      : minOutofRange
      ? minCompare
      : maxOutofRange
      ? maxCompare
      : "";

  useEffect(() => {
    props.setErrorPresent(isValueOutOfRange);
  }, [isValueOutOfRange]);
  useEffect(() => {
    if ((!prevMin && !prevMax) || prevQSFV !== qsfv) {
      setInitial();

      // update default values if parameter changed
    } else if (trigger && (props.min !== min || props.max !== max)) {
      setNewParam();

      // if values change, check for errors
    } else if ((prevMin && prevMin !== min) || (prevMax && prevMax !== max)) {
      setErrors(checkRangeError(min, max, prevMin && prevMin !== min));
    }

    if (
      (prevChange && prevChange !== change) ||
      (prevParam && prevParam !== props.parameter)
    )
      setTrigger(true);

    if (
      (lastReset && prevLastReset !== lastReset) ||
      (prevParam && prevParam !== props.parameter)
    )
      setDefault();
  });
  useEffect(() => {
    if (props.resetText) {
      setInitial();
      props.onConversion(min, max);
      setMax(props.rangeMax);
      setMin(props.rangeMin);
    }
  }, [props.resetText]);
  /**
   * @name handleBlur
   * @function
   * @description handles the Blur State
   */
  const handleBlur = (e) => {
    blurDragEnable(e);

    let input = e.target,
      value = determineValue(props.magnitude, input.value),
      isEmpty = EMPTY_INPUT.test(input.value);

    switch (input.dataset.type) {
      case MAX_TYPE:
        if (!isEmpty && max === value) return;
        if (isEmpty) value = props.max;
        setMax(value);
        props.onConversion(min, value);
        break;
      case MIN_TYPE:
        if (!isEmpty && min === value) return;
        if (isEmpty) value = props.min;
        setMin(value);
        props.onConversion(value, max);
        break;
      default:
        return;
    }
  };

  /**
   * @name handleChange
   * @function
   * @description handles the Input value change
   */
  const handleChange = (e) => {
    let value = e.target.value,
      isEmpty = EMPTY_INPUT.test(value);

    if (!isEmpty && INVALID_RANGE_CHARACTERS.test(value)) return;

    switch (e.target.dataset.type) {
      case MAX_TYPE:
        setMax(value);
        if (!isEmpty) props.onMaxChange(e);
        break;
      case MIN_TYPE:
        setMin(value);
        if (!isEmpty) props.onMinChange(e);
        break;
      default:
        return;
    }
  };
  /**
   * @name setInitial
   * @function
   * @description handles Initial resetting
   */
  const setInitial = () => {
    let vals = getInitialText(props.parameter, props.magnitude);
    if (!vals) return;

    setMax(vals.max === COMPARISONS.MAX ? props.max : vals.max);
    setMin(vals.min === COMPARISONS.MIN ? props.min : vals.min);
  };
  /**
   * @name setDefault
   * @function
   * @description Sets the default selection values in the fields
   */
  function setDefault() {
    setMax(props.rangeMax);
    setMin(props.rangeMin);
  }

  /**
   * This is distinct from setInitial as it will always set a new value regardless of the value in the hash
   * @name setNewParam
   * @function
   * @description Sets the initial selection values for the slider (read by the hash)
   */
  function setNewParam() {
    // get hash values
    let vals = getInitialText(props.parameter, props.magnitude);
    
    // if no hash values, set to default
    if (!vals) {
      vals = {
        max: props.rangeMax,
        min: props.rangeMin,
      };
    }
    
    // update state
    setMax(vals.max === COMPARISONS.MAX ? props.max : vals.max);
    setMin(vals.min === COMPARISONS.MIN ? props.min : vals.min);
    
    setTrigger(false);
  }

  const checkMinHigherMax = userEntryToNumber(min) > userEntryToNumber(max);

  const errorMessage = () => {
    
    if (checkMinHigherMax) {
      return `${localization.rangeErrorState} ${expandScientific(props.rangeMin)} to ${expandScientific(props.rangeMax)}`
    }
    if (minOutofRange || maxOutofRange) {
      return `${localization.acceptableValues} ${expandScientific(props.rangeMin)} to ${expandScientific(props.rangeMax)}`
    } 
  }

  return (
    <>
      <div className="range-filter-container__range-filter">
        <input
          className={`range-filter__range-from ${minOutofRange ? "error" : ""}`}
          type="text"
          data-type={MIN_TYPE}
          value={min}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          role="textbox"
          aria-label={`${props.name} ${localization.minValue} ${props.min} ${valueUnits(props.value)}`}
          aria-invalid={minOutofRange ? "true" : "false"}
        />
        <span>{localization.toLabel}</span>
        <input
          className={`range-filter__range-to ${maxOutofRange ? "error" : ""}`}
          type="text"
          data-type={MAX_TYPE}
          value={max}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          role="textbox"
          aria-label={`${props.name} ${localization.maxValue} ${props.max} ${valueUnits(props.value)}`}
          aria-invalid={maxOutofRange ? "true" : "false"}
        />
        <span>{props.value}</span>
      </div>
      {(isValueOutOfRange || checkMinHigherMax) && (
        <div
          className="error-states"
          role="alert"
          aria-live="polite"
          aria-atomic="true"
          aria-relevant="additions text"
        >
          <img src={errorIcon} alt="error-icon" className="error-icon" />
          {errorMessage()}
        </div>
      )}
    </>
  );
}

Text.defaultProps = {
  debounce: false,
  value: "",
};

Text.propTypes = {
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onConversion: PropTypes.func.isRequired,
  onMaxChange: PropTypes.func.isRequired,
  onMinChange: PropTypes.func.isRequired,
};

export default Text;
